import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Layout from '@/components/Layout';
import { PageContainer } from '@/components/ui/PageContainer';
import { PageHeader } from '@/components/ui/PageHeader';
import {
  Settings, Building2, Globe, Save, Loader2,
  MapPin, Plus, Pencil, Trash2, Star, Briefcase, X, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { US_STATES } from '@/lib/usStates';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_LOCATION = {
  label: '', address_line_1: '', address_line_2: '',
  city: '', state: '', postal_code: '', country: 'US'
};

function LocationForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(initial || { ...EMPTY_LOCATION });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-4" data-testid="location-form">
      <div>
        <Label className="text-slate-700 text-sm font-medium">Location Label *</Label>
        <Input data-testid="loc-label-input" value={f.label} onChange={e => set('label', e.target.value)}
          className="mt-1" placeholder='e.g. "Headquarters", "Savannah Office"' />
      </div>
      <div>
        <Label className="text-slate-700 text-sm font-medium">Address Line 1</Label>
        <Input data-testid="loc-addr1-input" value={f.address_line_1} onChange={e => set('address_line_1', e.target.value)}
          className="mt-1" placeholder="100 Main Street" />
      </div>
      <div>
        <Label className="text-slate-700 text-sm font-medium">Address Line 2</Label>
        <Input data-testid="loc-addr2-input" value={f.address_line_2} onChange={e => set('address_line_2', e.target.value)}
          className="mt-1" placeholder="Suite 200 (optional)" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="col-span-2 sm:col-span-1">
          <Label className="text-slate-700 text-sm font-medium">City</Label>
          <Input data-testid="loc-city-input" value={f.city} onChange={e => set('city', e.target.value)}
            className="mt-1" placeholder="Savannah" />
        </div>
        <div>
          <Label className="text-slate-700 text-sm font-medium">State</Label>
          <select data-testid="loc-state-input" value={f.state}
            onChange={e => set('state', e.target.value)}
            className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm mt-1">
            <option value="">Select state...</option>
            {US_STATES.map(s => (
              <option key={s.code} value={s.code}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-slate-700 text-sm font-medium">ZIP</Label>
          <Input data-testid="loc-zip-input" value={f.postal_code} onChange={e => set('postal_code', e.target.value)}
            className="mt-1" placeholder="31401" />
        </div>
        <div>
          <Label className="text-slate-700 text-sm font-medium">Country</Label>
          <Input data-testid="loc-country-input" value={f.country} onChange={e => set('country', e.target.value)}
            className="mt-1" placeholder="US" />
        </div>
      </div>
      <div className="flex gap-2 justify-end pt-1">
        <Button variant="outline" size="sm" onClick={onCancel} className="border-slate-300 text-slate-600"
          data-testid="loc-cancel-btn">
          <X className="w-3.5 h-3.5 mr-1" /> Cancel
        </Button>
        <Button size="sm" onClick={() => onSave(f)} disabled={saving || !f.label.trim()}
          className="bg-brand hover:bg-brand-hover text-white" data-testid="loc-save-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
          Save
        </Button>
      </div>
    </div>
  );
}

function LocationCard({ loc, isHQ, isDefault, onEdit, onDelete, onSetHQ, onSetDefault }) {
  const parts = [loc.city, loc.state].filter(Boolean).join(', ');
  return (
    <div className="flex items-start justify-between p-4 rounded-lg border border-slate-200 bg-white hover:border-slate-300 transition-colors"
      data-testid={`location-card-${loc.location_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900 text-sm">{loc.label}</span>
          {isHQ && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200"
              data-testid="hq-badge">
              <Star className="w-2.5 h-2.5" /> HQ
            </span>
          )}
          {isDefault && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-brand/10 text-brand border border-brand/20"
              data-testid="default-badge">
              <Briefcase className="w-2.5 h-2.5" /> Default
            </span>
          )}
        </div>
        {loc.address_line_1 && <p className="text-xs text-slate-500 mt-0.5">{loc.address_line_1}{loc.address_line_2 ? `, ${loc.address_line_2}` : ''}</p>}
        {parts && <p className="text-xs text-slate-500">{parts}{loc.postal_code ? ` ${loc.postal_code}` : ''}</p>}
      </div>
      <div className="flex items-center gap-1 ml-3 shrink-0">
        {!isHQ && (
          <Button variant="ghost" size="sm" onClick={onSetHQ} className="h-7 px-2 text-xs text-slate-500 hover:text-amber-700"
            title="Set as Headquarters" data-testid={`set-hq-${loc.location_id}`}>
            <Star className="w-3 h-3" />
          </Button>
        )}
        {!isDefault && (
          <Button variant="ghost" size="sm" onClick={onSetDefault} className="h-7 px-2 text-xs text-slate-500 hover:text-brand"
            title="Set as Default Job Location" data-testid={`set-default-${loc.location_id}`}>
            <Briefcase className="w-3 h-3" />
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onEdit} className="h-7 px-2 text-xs text-slate-500 hover:text-slate-900"
          data-testid={`edit-loc-${loc.location_id}`}>
          <Pencil className="w-3 h-3" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete} className="h-7 px-2 text-xs text-slate-500 hover:text-red-600"
          data-testid={`delete-loc-${loc.location_id}`}>
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

export default function EmployerSettings() {
  const [user, setUser] = useState(null);
  const [employer, setEmployer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ company_name: '', company_description: '', website: '' });

  // Location state
  const [locations, setLocations] = useState([]);
  const [hqId, setHqId] = useState(null);
  const [defaultId, setDefaultId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [locSaving, setLocSaving] = useState(false);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/employer/locations`, { withCredentials: true });
      const active = (res.data.locations || []).filter(l => l.is_active !== false);
      setLocations(active);
      setHqId(res.data.headquarters_location_id);
      setDefaultId(res.data.default_job_location_id);
    } catch { /* silent — not all employers will have this */ }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [userRes, empRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/employer/profile`, { withCredentials: true }).catch(() => ({ data: null }))
        ]);
        setUser(userRes.data);
        if (empRes.data) {
          setEmployer(empRes.data);
          setForm({
            company_name: empRes.data.company_name || '',
            company_description: empRes.data.company_description || '',
            website: empRes.data.website || ''
          });
        }
        await fetchLocations();
      } catch { /* auth will redirect */ }
      setLoading(false);
    };
    load();
  }, []);

  const handleSave = async () => {
    if (!form.company_name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/employer/profile`, {
        company_name: form.company_name.trim(),
        company_description: form.company_description.trim(),
        website: form.website.trim() || null
      }, { withCredentials: true });
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleAddLocation = async (data) => {
    if (!data.label.trim()) { toast.error('Label is required'); return; }
    setLocSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/employer/locations`, data, { withCredentials: true });
      toast.success('Location added');
      setShowAddForm(false);
      await fetchLocations();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
    finally { setLocSaving(false); }
  };

  const handleEditLocation = async (data) => {
    setLocSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/employer/locations/${editingId}`, data, { withCredentials: true });
      toast.success('Location updated');
      setEditingId(null);
      await fetchLocations();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setLocSaving(false); }
  };

  const handleDeleteLocation = async (id) => {
    if (!window.confirm('Deactivate this location? Existing jobs using it will keep their data.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/employer/locations/${id}`, { withCredentials: true });
      toast.success('Location removed');
      await fetchLocations();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove'); }
  };

  const handleSetHQ = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/employer/locations/set-headquarters`,
        { location_id: id }, { withCredentials: true });
      setHqId(id);
      toast.success('Headquarters updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSetDefault = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/employer/locations/set-default-job`,
        { location_id: id }, { withCredentials: true });
      setDefaultId(id);
      toast.success('Default job location updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleUseHQAsDefault = async () => {
    if (!hqId) { toast.error('Set a headquarters first'); return; }
    await handleSetDefault(hqId);
  };

  if (loading) {
    return (
      <Layout user={user}>
        <PageContainer>
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-brand" />
          </div>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <PageContainer>
        <PageHeader
          icon={Settings}
          title="Employer Settings"
          subtitle="Manage your company profile and account details"
        />

        {/* Account Info — read-only */}
        <Card className="p-6 border border-slate-200 mb-6" data-testid="account-info-card">
          <h2 className="text-base font-semibold text-slate-900 mb-4">Account</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Name</p>
              <p className="text-sm text-slate-900 font-medium">{user?.name || 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Email</p>
              <p className="text-sm text-slate-900 font-medium">{user?.email || 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Status</p>
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                employer?.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
              }`}>{employer?.status || 'Unknown'}</span>
            </div>
            {employer?.region_id && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Region</p>
                <p className="text-sm text-slate-900 font-medium capitalize">{employer.region_id.replace('region_', '').replace(/_/g, ' ')}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Company Profile — editable */}
        {employer && (
          <Card className="p-6 border border-slate-200 mb-6" data-testid="company-profile-card">
            <h2 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-brand" /> Company Profile
            </h2>
            <div className="space-y-5">
              <div>
                <Label htmlFor="company_name" className="text-slate-700 text-sm font-medium">Company Name</Label>
                <Input id="company_name" data-testid="company-name-input"
                  value={form.company_name}
                  onChange={e => setForm(prev => ({ ...prev, company_name: e.target.value }))}
                  className="mt-1.5" placeholder="Enter company name" />
              </div>
              <div>
                <Label htmlFor="company_desc" className="text-slate-700 text-sm font-medium">Company Description</Label>
                <Textarea id="company_desc" data-testid="company-desc-input"
                  value={form.company_description}
                  onChange={e => setForm(prev => ({ ...prev, company_description: e.target.value }))}
                  className="mt-1.5 min-h-[120px]" placeholder="Describe your company..." />
              </div>
              <div>
                <Label htmlFor="website" className="text-slate-700 text-sm font-medium flex items-center gap-1">
                  <Globe className="w-3.5 h-3.5" /> Website
                </Label>
                <Input id="website" data-testid="website-input"
                  value={form.website}
                  onChange={e => setForm(prev => ({ ...prev, website: e.target.value }))}
                  className="mt-1.5" placeholder="https://yourcompany.com" />
              </div>
              <div className="flex justify-end pt-2">
                <Button data-testid="save-settings-btn" onClick={handleSave} disabled={saving}
                  className="bg-brand hover:bg-brand-hover text-white">
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Saved Locations */}
        {employer && (
          <Card className="p-6 border border-slate-200" data-testid="locations-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-brand" /> Company Locations
              </h2>
              {!showAddForm && !editingId && (
                <Button size="sm" onClick={() => setShowAddForm(true)}
                  className="bg-brand hover:bg-brand-hover text-white h-8 text-xs"
                  data-testid="add-location-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Location
                </Button>
              )}
            </div>

            {/* HQ as default convenience */}
            {locations.length > 0 && hqId && hqId !== defaultId && (
              <button onClick={handleUseHQAsDefault}
                className="mb-4 text-xs text-brand hover:text-brand-hover underline underline-offset-2"
                data-testid="use-hq-as-default-btn">
                Use headquarters as default job location
              </button>
            )}

            {/* Add form */}
            {showAddForm && (
              <div className="mb-4 p-4 rounded-lg border border-brand/30 bg-brand/5">
                <p className="text-sm font-medium text-slate-800 mb-3">New Location</p>
                <LocationForm onSave={handleAddLocation} onCancel={() => setShowAddForm(false)} saving={locSaving} />
              </div>
            )}

            {/* Location list */}
            {locations.length === 0 && !showAddForm ? (
              <div className="text-center py-10 text-slate-400" data-testid="no-locations-empty">
                <MapPin className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No saved locations yet</p>
                <p className="text-xs mt-1">Add your company locations to quickly assign them when posting jobs.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {locations.map(loc => (
                  editingId === loc.location_id ? (
                    <div key={loc.location_id} className="p-4 rounded-lg border border-brand/30 bg-brand/5">
                      <p className="text-sm font-medium text-slate-800 mb-3">Edit Location</p>
                      <LocationForm
                        initial={{
                          label: loc.label || '', address_line_1: loc.address_line_1 || '',
                          address_line_2: loc.address_line_2 || '', city: loc.city || '',
                          state: loc.state || '', postal_code: loc.postal_code || '',
                          country: loc.country || 'US'
                        }}
                        onSave={handleEditLocation}
                        onCancel={() => setEditingId(null)}
                        saving={locSaving}
                      />
                    </div>
                  ) : (
                    <LocationCard key={loc.location_id} loc={loc}
                      isHQ={hqId === loc.location_id}
                      isDefault={defaultId === loc.location_id}
                      onEdit={() => setEditingId(loc.location_id)}
                      onDelete={() => handleDeleteLocation(loc.location_id)}
                      onSetHQ={() => handleSetHQ(loc.location_id)}
                      onSetDefault={() => handleSetDefault(loc.location_id)}
                    />
                  )
                ))}
              </div>
            )}
          </Card>
        )}
      </PageContainer>
    </Layout>
  );
}
